import {
  useEffect,
  useId,
  useRef,
  type KeyboardEvent
} from "react";
import { Headphones, Volume2 } from "lucide-react";

import type { Difficulty } from "@/domain";
import type { ActivityHostRenderContext } from "@/features/activity-host";
import type { CurriculumAudioPlayer } from "@/features/audio";

import type {
  SoundBubbleAction,
  SoundBubbleRenderModel
} from "../adapters";
import {
  ChineseDifficultyBadge,
  ChineseFeedbackNotice
} from "./shared";

export interface SoundBubblesRendererProps {
  readonly audioPlayer: CurriculumAudioPlayer;
  readonly context: ActivityHostRenderContext<
    SoundBubbleAction,
    SoundBubbleRenderModel
  >;
  readonly difficulty: Difficulty;
}

export function SoundBubblesRenderer({
  audioPlayer,
  context,
  difficulty
}: SoundBubblesRendererProps) {
  const questionId = useId();
  const choiceRefs = useRef(new Map<string, HTMLButtonElement>());
  const { dispatch, getActivationProps, model } = context;
  const visibleChoices = model.choices.filter(({ visible }) => visible);
  const firstChoiceId = visibleChoices[0]?.id ?? null;
  const lastChoiceId = visibleChoices.at(-1)?.id ?? null;

  useEffect(() => {
    if (model.focusedChoiceId !== null) {
      choiceRefs.current.get(model.focusedChoiceId)?.focus();
    }
  }, [model.focusedChoiceId]);

  function handleKeyDown(
    event: KeyboardEvent<HTMLButtonElement>,
    choiceId: string
  ): void {
    switch (event.key) {
      case "ArrowDown":
      case "ArrowRight":
        event.preventDefault();
        dispatch(
          { type: "move-focus", direction: "next" },
          "keyboard"
        );
        break;
      case "ArrowLeft":
      case "ArrowUp":
        event.preventDefault();
        dispatch(
          { type: "move-focus", direction: "previous" },
          "keyboard"
        );
        break;
      case "Home":
        event.preventDefault();
        if (firstChoiceId !== null) {
          dispatch(
            { type: "focus-choice", choiceId: firstChoiceId },
            "keyboard"
          );
        }
        break;
      case "End":
        event.preventDefault();
        if (lastChoiceId !== null) {
          dispatch(
            { type: "focus-choice", choiceId: lastChoiceId },
            "keyboard"
          );
        }
        break;
      case "Enter":
      case " ":
        event.preventDefault();
        dispatch(
          { type: "choose", choiceId, inputMethod: "keyboard" },
          "keyboard"
        );
        break;
    }
  }

  return (
    <section
      aria-labelledby={questionId}
      className="chinese-player__activity chinese-player__sound"
      data-difficulty={difficulty}
      data-reduced-motion={context.reducedMotion}
    >
      <header className="chinese-player__activity-heading">
        <span aria-hidden="true" className="chinese-player__activity-mark">
          <Headphones />
        </span>
        <div>
          <ChineseDifficultyBadge difficulty={difficulty} />
          <h2 id={questionId}>{model.question}</h2>
        </div>
      </header>

      <div
        aria-labelledby={questionId}
        className="chinese-player__sound-choices"
        role="group"
      >
        {visibleChoices.map((choice) => (
          <button
            {...getActivationProps((inputMethod) => ({
              type: "choose",
              choiceId: choice.id,
              inputMethod
            }))}
            aria-label={"选择" + choice.label}
            aria-pressed={choice.selected}
            className="chinese-player__sound-choice"
            data-hinted={choice.hinted}
            data-selected={choice.selected}
            key={choice.id}
            onFocus={() =>
              dispatch(
                { type: "focus-choice", choiceId: choice.id },
                "keyboard"
              )
            }
            onKeyDown={(event) => handleKeyDown(event, choice.id)}
            ref={(node) => {
              if (node === null) {
                choiceRefs.current.delete(choice.id);
              } else {
                choiceRefs.current.set(choice.id, node);
              }
            }}
            tabIndex={
              choice.focused ||
              (model.focusedChoiceId === null &&
                choice.id === firstChoiceId)
                ? 0
                : -1
            }
            type="button"
          >
            <Volume2 aria-hidden="true" />
            <span>{choice.label}</span>
            {choice.hinted ? (
              <small>听音提示</small>
            ) : null}
          </button>
        ))}
      </div>

      <ChineseFeedbackNotice
        audioPlayer={audioPlayer}
        enabled={!context.isPaused}
        feedback={model.feedback}
      />
    </section>
  );
}
